import React, { useEffect, useRef, useState } from 'react';
import '../prices/Price.css';
import './AddOnCatalog.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import AddOnChips from './AddOnChips';
import AddOnEditorPopup, { ADD_ON_EDITOR_POPUP } from './AddOnEditorPopup';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useAddOns, { reloadAddOns } from '../../hooks/useAddOns';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import {
  addOnApiErrorMessage,
  addOnColorStyle,
  addOnPlaceholderUsage,
  formatAddOnPrice,
  formatGroupPrices,
  hasAddOnColor,
  isAddOnUsed,
  placeholderTokens,
} from '../../util/addOns';
import { isAdmin } from '../../util/auth';

const DELETE_POPUP = 'confirmdeleteaddon';
const DELETE_ERROR_POPUP = 'addondeleteerror';

// "max. 8 je Spieltag" / "unbegrenzt"
const maxPerEventLabel = (max) => (Number(max) > 0 ? `max. ${max} je Spieltag` : 'unbegrenzt');

// Standard values copied into new processes (price · max · Homepage).
function standardLine(addOn) {
  if (addOn.price == null) return 'entfällt – nur an Locations mit eigener Abweichung';
  return [
    formatAddOnPrice(addOn.price),
    maxPerEventLabel(addOn.maxQuantity),
    addOn.bookableOnHomepage ? 'Homepage' : null,
  ].filter(Boolean).join(' · ');
}

// Live on-site values ("Im Event (gilt sofort)") — null when the standard does not offer it on site.
function onSiteLine(addOn) {
  if (addOn.price == null || !addOn.bookableOnSite) return null;
  return [formatAddOnPrice(addOn.price), maxPerEventLabel(addOn.maxQuantity)].join(' · ');
}

// Own values of one location: "20,00 € · max. 10 je Spieltag · Homepage · im Event" / "entfällt".
function locationLine(setting) {
  if (setting.price == null) return 'entfällt';
  return [
    formatAddOnPrice(setting.price),
    maxPerEventLabel(setting.maxQuantity),
    setting.bookableOnHomepage ? 'Homepage' : null,
    setting.bookableOnSite ? 'im Event' : null,
  ].filter(Boolean).join(' · ');
}

const offeredOnSiteSomewhere = (addOn) =>
  (addOn.price != null && addOn.bookableOnSite)
  || addOn.locationSettings.some(s => s.price != null && s.bookableOnSite);

// Usage of an entry (admin): "1 Prozessvorlage · 2 Prozesse · 1 Textbaustein" / "Nicht genutzt" / "In Verwendung"
// (refused: the API refused the deletion although the loaded counts were zero — assigned meanwhile).
function usageLabel(addOn, refused) {
  const templates = addOn.templateCount;
  const processes = addOn.processCount;
  const textblocks = addOnPlaceholderUsage(addOn).length;
  const parts = [];
  if (templates > 0) parts.push(templates === 1 ? '1 Prozessvorlage' : `${templates} Prozessvorlagen`);
  if (processes > 0) parts.push(processes === 1 ? '1 Prozess' : `${processes} Prozesse`);
  if (textblocks > 0) parts.push(textblocks === 1 ? '1 Textbaustein' : `${textblocks} Textbausteine`);
  if (parts.length > 0) return parts.join(' · ');
  return refused ? 'In Verwendung' : 'Nicht genutzt';
}

function deleteErrorMessage(err) {
  // 404 bodies are ProblemDetails (English title) — use our own text.
  if (err?.response?.status === 404) return 'Der Zusatz existiert nicht mehr.';
  const fallback = err?.response?.status === 409
    ? 'Der Zusatz wird noch verwendet und kann nicht gelöscht werden.'
    : 'Löschen fehlgeschlagen.';
  return addOnApiErrorMessage(err, fallback);
}

/**
 * Section "Zusätze" of the price list (/prices): the add-on catalog — name, description, the standard values that
 * new processes copy, the live on-site values ("Im Event"), fixed group prices and the deviations per location.
 * Every signed-in user reads it (offline from the reference cache); admins create/edit (AddOnEditorPopup) and
 * delete unused entries (the API refuses referenced ones and says where). Data only via the shared useAddOns list
 * (patched locally after saves) and the shared group list.
 */
function AddOnCatalog() {
  const { addOns, loaded, loading, failed, ensureLoaded, upsert, remove } = useAddOns();
  const { groups } = useParticipantGroups();
  const { isOffline, onlineEpoch } = useOffline();
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const admin = isAdmin();

  // Edited row (null = create). Kept after closing so the popup keeps its title during the exit animation.
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  // Ids whose deletion the API refused (409) — they no longer offer deleting on this page.
  const [refusedIds, setRefusedIds] = useState(() => new Set());

  // Admins: fetch once per visit — the usage counts (delete button, usage label, key lock) change through
  // templates, processes and textblocks, also by other admins. Shares the request with ensureLoaded below; the
  // loaded list stays on screen meanwhile. Offline the cached copy is shown and the refresh waits for the
  // connection.
  const refreshedRef = useRef(false);
  useEffect(() => {
    if (refreshedRef.current || !admin || isOffline) return;
    refreshedRef.current = true;
    reloadAddOns();
  }, [admin, isOffline]);

  // Offline the list comes from the reference cache; after "Erneut verbinden" a failed first load is retried
  // (ensureLoaded is a no-op once loaded — the hook refreshes a loaded list itself).
  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded, isOffline, onlineEpoch]);

  const openEditor = (addOn = null) => {
    setEditing(addOn);
    openPopup(ADD_ON_EDITOR_POPUP, addOn?.id ?? 'new');
  };

  const askDelete = (addOn) => {
    setPendingDelete(addOn);
    openPopup(DELETE_POPUP, addOn.id);
  };

  const handleConfirmDelete = () => {
    const addOn = pendingDelete;
    closePopup();
    if (!addOn) return;
    startSuccessBarLoading('Zusatz wird gelöscht');
    api.delete(`/AddOn/Delete/${addOn.id}`)
      .then(() => {
        remove(addOn.id);
        startSuccessBarSuccess('Zusatz gelöscht');
      })
      .catch(err => {
        closeSuccessBar();
        // Changed meanwhile (e.g. by another admin): bring the row in line with the server.
        const status = err?.response?.status;
        if (status === 404) remove(addOn.id);
        if (status === 409) setRefusedIds(prev => new Set(prev).add(addOn.id));
        setDeleteError(deleteErrorMessage(err));
        openPopup(DELETE_ERROR_POPUP, addOn.id);
      });
  };

  const count = addOns.length;
  let subtitle = '';
  if (!loaded) subtitle = loading ? 'Lädt …' : '';
  else if (count === 0) subtitle = 'Noch keine Zusätze angelegt';
  else subtitle = `${count === 1 ? '1 Zusatz' : `${count} Zusätze`}${admin ? ' · Löschen nur bei ungenutzten' : ''}`;

  const flagItems = (addOn) => {
    const items = [];
    if (addOn.waitlistWhenFull) {
      items.push({
        key: 'waitlist',
        label: 'Ausgebucht → Warteliste',
        tooltip: 'Ausgebucht: Anmeldungen mit diesem Zusatz kommen auf die Zusatz-Warteliste.',
      });
    }
    if (addOn.maxPerPerson > 1) items.push({ key: 'perPerson', label: `bis ${addOn.maxPerPerson} je Person` });
    if (admin && addOn.placeholderKey) {
      items.push({
        key: 'placeholder',
        label: `Kürzel ${addOn.placeholderKey}`,
        tooltip: `Mail-Platzhalter: ${placeholderTokens(addOn.placeholderKey).join(', ')}`,
      });
    }
    if (!offeredOnSiteSomewhere(addOn) && !isAddOnUsed(addOn)) {
      items.push({
        key: 'infoOnly',
        label: 'Nur Preisinfo',
        tooltip: 'Weder im Event buchbar noch in einer Prozessvorlage oder einem Prozess – steht nur in der Preisliste.',
      });
    }
    return items;
  };

  const renderRow = (addOn) => {
    const refused = refusedIds.has(addOn.id);
    const deletable = !isAddOnUsed(addOn) && addOnPlaceholderUsage(addOn).length === 0 && !refused;
    const onSite = onSiteLine(addOn);
    const groupPrices = formatGroupPrices(addOn, groups);
    const flags = flagItems(addOn);
    const colored = hasAddOnColor(addOn.colorKey);
    return (
      <li key={addOn.id} className="addOnCatalogRow">
        <span
          className={`material-symbols-rounded addOnCatalogIcon${colored ? ' isColored' : ''}`}
          style={addOnColorStyle(addOn.colorKey)}
          aria-hidden="true"
        >
          inventory_2
        </span>
        <div className="addOnCatalogText">
          <span className="addOnCatalogName">{addOn.name}</span>
          {addOn.description && <span className="addOnCatalogDescription">{addOn.description}</span>}
          <dl className="addOnCatalogMeta">
            <div className="addOnCatalogMetaPair">
              <dt>Standard für neue Prozesse</dt>
              <dd>{standardLine(addOn)}</dd>
            </div>
            {onSite && (
              <div className="addOnCatalogMetaPair">
                <dt>Im Event (gilt sofort)</dt>
                <dd>{onSite}</dd>
              </div>
            )}
            {groupPrices && (
              <div className="addOnCatalogMetaPair">
                <dt>Gruppenpreise</dt>
                <dd>{groupPrices}</dd>
              </div>
            )}
            {addOn.locationSettings.map(s => (
              <div key={s.locationId} className="addOnCatalogMetaPair isLocation">
                <dt>
                  <span className="material-symbols-rounded" aria-hidden="true">location_on</span>
                  <span>{s.locationName}</span>
                </dt>
                <dd>{locationLine(s)}</dd>
              </div>
            ))}
          </dl>
          {flags.length > 0 && <AddOnChips compact items={flags} ariaLabel="Eigenschaften" className="addOnCatalogFlags" />}
        </div>
        {admin && (
          <span className={`addOnCatalogUsage${deletable ? ' isUnused' : ''}`}>{usageLabel(addOn, refused)}</span>
        )}
        {admin && (
          <span className="addOnCatalogActions">
            <button
              type="button"
              className="addOnCatalogBtn"
              onClick={() => openEditor(addOn)}
              disabled={isOffline}
              aria-label={`${addOn.name} bearbeiten`}
              title={isOffline ? 'Offline nicht verfügbar' : 'Bearbeiten'}
            >
              <span className="material-symbols-rounded" aria-hidden="true">edit</span>
            </button>
            {deletable && (
              <button
                type="button"
                className="addOnCatalogBtn isDanger"
                onClick={() => askDelete(addOn)}
                disabled={isOffline}
                aria-label={`${addOn.name} löschen`}
                title={isOffline ? 'Offline nicht verfügbar' : 'Löschen (nur ungenutzte Zusätze)'}
              >
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              </button>
            )}
          </span>
        )}
      </li>
    );
  };

  let content;
  if (!loaded) {
    if (failed) {
      content = (
        <div className="addOnCatalogState">
          <p className="addOnCatalogMessage">
            {isOffline
              ? 'Offline nicht verfügbar – die Zusätze wurden auf diesem Gerät noch nicht geladen.'
              : 'Zusätze konnten nicht geladen werden.'}
          </p>
          {!isOffline && <ActionButton icon="refresh" label="Erneut laden" onClick={() => ensureLoaded()} />}
        </div>
      );
    } else {
      content = <p className="addOnCatalogMessage">Zusätze werden geladen …</p>;
    }
  } else if (count === 0) {
    content = (
      <p className="addOnCatalogMessage">
        {admin ? 'Noch keine Zusätze – lege den ersten mit „Neuer Zusatz“ an.' : 'Noch keine Zusätze angelegt.'}
      </p>
    );
  } else {
    content = <ul className="addOnCatalogList">{addOns.map(renderRow)}</ul>;
  }

  return (
    <React.Fragment>
      <main className="eventColumnMain addOnCatalog" aria-label="Zusätze">
        <div className="pricesSectionHeader">
          <div className="pricesSectionHeaderText">
            <h3 className="pricesSectionTitle">Zusätze</h3>
            {subtitle && <span className="pricesSectionHint">{subtitle}</span>}
          </div>
          {admin && (
            <ActionButton
              icon="add"
              label="Neuer Zusatz"
              type={3}
              onClick={() => openEditor()}
              disabled={isOffline || !loaded}
              title={isOffline ? 'Offline nicht verfügbar' : undefined}
            />
          )}
        </div>
        <p className="formFieldHint">
          {admin
            ? 'Anmelde-Zusätze und ihre Preise gelten je Prozess – siehe Prozessseite. Hier legst du fest, was neue '
              + 'Prozesse übernehmen und was im Event als Artikel verkauft wird.'
            : 'Bei der Anmeldung gelten die Preise des Prozesses eines Spieltags – sie können von dieser Liste '
              + 'abweichen. Artikel im Event kosten immer den Preis von hier.'}
        </p>
        {content}
      </main>

      {admin && (
        <AddOnEditorPopup editing={editing} addOns={addOns} onSaved={upsert} onRemoved={remove} />
      )}

      <Popup
        name={DELETE_POPUP}
        headerText="Zusatz löschen"
        confirmText="Löschen"
        type="confirmaction"
        onConfirm={handleConfirmDelete}
        withCancel
      >
        <p>Soll der Zusatz <b>{pendingDelete?.name}</b> dauerhaft gelöscht werden?</p>
        <p className="formFieldHint">
          Bereits gebuchte Zusätze und Artikel bleiben mit Name und Preis erhalten (auch in der Statistik); offene
          Anmeldewünsche für diesen Zusatz entfallen.
        </p>
        <p className="formFieldHint">
          Nicht möglich, solange eine Prozessvorlage oder ein Prozess ihn verwendet oder Textbausteine seine
          Platzhalter enthalten.
        </p>
      </Popup>
      <Popup name={DELETE_ERROR_POPUP} headerText="Löschen fehlgeschlagen" confirmText="Ok" type="error">
        <p>{deleteError}</p>
      </Popup>
    </React.Fragment>
  );
}

export default AddOnCatalog;
